import { useEffect, useId, useState } from 'react'
import type { FormEvent } from 'react'
import { Check, Copy, RefreshCw } from 'lucide-react'
import { MIN_PASSWORD_LENGTH } from '@shared/constants'
import { Role } from '@shared/types'
import { Button, Input, Modal, Select } from '@/components/ui'
import { api, errorMessage, isApiError } from '@/lib/api'

/* Ambiguous glyphs (l/I/1, O/0) are left out: these passwords get read off a
   screen and typed somewhere else by hand. */
const LOWER = 'abcdefghijkmnopqrstuvwxyz'
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const DIGITS = '23456789'
const SYMBOLS = '!@#$%^&*-_=+?'
const POOLS = [LOWER, UPPER, DIGITS, SYMBOLS]
const ALL = POOLS.join('')

const GENERATED_LENGTH = 20

function pick(pool: string, random: number): string {
  return pool[random % pool.length] ?? ALL[0] ?? 'x'
}

/**
 * A random password strong enough that nobody is tempted to invent one. The
 * first character of each pool is guaranteed, then the whole thing is shuffled,
 * so it always satisfies a mixed-character policy without a predictable shape.
 */
export function generatePassword(length: number = GENERATED_LENGTH): string {
  const size = Math.max(MIN_PASSWORD_LENGTH, length)
  const random = new Uint32Array(size * 2)
  crypto.getRandomValues(random)

  const chars: string[] = []
  for (let index = 0; index < size; index += 1) {
    const pool = index < POOLS.length ? (POOLS[index] ?? ALL) : ALL
    chars.push(pick(pool, random[index] ?? 0))
  }

  for (let index = chars.length - 1; index > 0; index -= 1) {
    const swap = (random[size + index] ?? 0) % (index + 1)
    const current = chars[index]
    const other = chars[swap]
    if (current === undefined || other === undefined) continue
    chars[index] = other
    chars[swap] = current
  }

  return chars.join('')
}

export interface SecretRevealProps {
  label: string
  value: string
}

/**
 * A credential shown once, with a copy button. Lives here rather than in the
 * UI kit because it is only ever correct for hand-over-out-of-band secrets.
 */
export function SecretReveal({ label, value }: SecretRevealProps): React.ReactElement {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 2000)
    return () => window.clearTimeout(timer)
  }, [copied])

  const copy = async (): Promise<void> => {
    // An insecure origin has no clipboard API at all, so say so rather than
    // reporting a copy that never happened.
    if (!navigator.clipboard) {
      setFailed(true)
      return
    }
    try {
      await navigator.clipboard.writeText(value)
      setFailed(false)
      setCopied(true)
    } catch {
      setFailed(true)
    }
  }

  return (
    <div>
      <span className="mb-1.5 block text-[11px] font-semibold tracking-[0.07em] text-fg-subtle uppercase">
        {label}
      </span>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded-control border border-line bg-sunken px-3 py-2 font-mono text-sm break-all text-fg select-all">
          {value}
        </code>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="shrink-0"
          iconLeft={copied ? <Check /> : <Copy />}
          onClick={() => {
            void copy()
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <span className="sr-only" aria-live="polite">
        {copied ? `${label} copied to the clipboard` : ''}
      </span>
      {failed ? (
        <p className="mt-1.5 text-xs font-medium text-bad-600 dark:text-bad-300">
          Copying is not available here — select the text and copy it manually.
        </p>
      ) : null}
    </div>
  )
}

const ROLE_OPTIONS = [
  { value: Role.User, label: 'User — connects their own sites' },
  { value: Role.Admin, label: 'Admin — also manages users and instance settings' }
]

interface FieldErrors {
  name?: string
  email?: string
  password?: string
  role?: string
  form?: string
}

function fieldErrorsFrom(details: Record<string, string[]> | undefined): FieldErrors {
  const next: FieldErrors = {}
  if (!details) return next

  for (const [key, messages] of Object.entries(details)) {
    const first = messages[0]
    if (!first) continue
    if (key === 'name' || key === 'email' || key === 'password' || key === 'role') next[key] = first
    else next.form = first
  }

  return next
}

export interface InviteUserDialogProps {
  open: boolean
  onClose: () => void
  /** Fired once the account exists, so the list can refetch from the server. */
  onCreated: () => void
}

export default function InviteUserDialog({
  open,
  onClose,
  onCreated
}: InviteUserDialogProps): React.ReactElement {
  const formId = useId()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>(Role.User)
  const [password, setPassword] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState<{ name: string, email: string } | null>(null)

  // Reset on open rather than on close, so the credentials stay readable while
  // the dialog is animating away.
  useEffect(() => {
    if (!open) return
    setName('')
    setEmail('')
    setRole(Role.User)
    setPassword(generatePassword())
    setErrors({})
    setSubmitting(false)
    setCreated(null)
  }, [open])

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()

    const trimmedName = name.trim()
    const trimmedEmail = email.trim()
    const next: FieldErrors = {}

    if (!trimmedName) next.name = 'Enter a name'
    if (!trimmedEmail) next.email = 'Enter an email address'
    if (password.length < MIN_PASSWORD_LENGTH) {
      next.password = `Must be at least ${MIN_PASSWORD_LENGTH} characters`
    } else if (password.toLowerCase() === trimmedEmail.toLowerCase()) {
      next.password = 'The password cannot be the email address'
    }

    setErrors(next)
    if (Object.keys(next).length > 0) return

    setSubmitting(true)
    try {
      await api.post<unknown>('/admin/users', {
        name: trimmedName,
        email: trimmedEmail,
        password,
        role
      })
      setCreated({ name: trimmedName, email: trimmedEmail })
      onCreated()
    } catch (error) {
      const mapped = isApiError(error) ? fieldErrorsFrom(error.details) : {}
      setErrors(
        Object.keys(mapped).length > 0 ? mapped : { form: errorMessage(error) }
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={created ? 'Account created' : 'New user'}
      description={
        created
          ? undefined
          : 'Argus sends no email, so you will be handing these credentials over yourself.'
      }
      footer={
        created ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form={formId} loading={submitting}>
              Create account
            </Button>
          </>
        )
      }
    >
      {created ? (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-fg-muted">
            <span className="font-semibold text-fg">{created.name}</span> can sign in now. Nothing
            has been emailed to them — Argus has no mail sender, which is a real constraint of this
            deployment, not an oversight. Pass these on through a channel you trust.
          </p>

          <SecretReveal label="Email" value={created.email} />
          <SecretReveal label="Password" value={password} />

          <p className="text-xs leading-relaxed text-fg-subtle">
            This is the only time the password is shown. Argus stores nothing but a hash of it, so
            once this dialog closes nobody — not even an admin — can read it back. If it goes
            missing, reset the password from the users list. Ask them to set their own from the
            account menu after they sign in.
          </p>
        </div>
      ) : (
        <form
          id={formId}
          noValidate
          className="space-y-4"
          onSubmit={(event) => {
            void submit(event)
          }}
        >
          <Input
            label="Name"
            value={name}
            error={errors.name}
            autoComplete="off"
            data-autofocus
            onChange={(event) => setName(event.target.value)}
          />

          <Input
            label="Email"
            type="email"
            value={email}
            error={errors.email}
            hint="Used to sign in. It is never contacted by Argus."
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setEmail(event.target.value)}
          />

          <Select
            label="Role"
            value={role}
            error={errors.role}
            options={ROLE_OPTIONS}
            hint="Admins can see and change every account plus the instance-wide settings. They still only see their own sites and keys."
            onChange={(event) =>
              setRole(event.target.value === Role.Admin ? Role.Admin : Role.User)
            }
          />

          <div className="space-y-2">
            <Input
              label="Password"
              type="text"
              value={password}
              error={errors.password}
              hint={`At least ${MIN_PASSWORD_LENGTH} characters. Shown in plain text because you have to relay it.`}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
              onChange={(event) => setPassword(event.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft={<RefreshCw />}
              onClick={() => setPassword(generatePassword())}
            >
              Generate a strong one
            </Button>
          </div>

          {errors.form ? (
            <p className="text-sm font-medium text-bad-600 dark:text-bad-300">{errors.form}</p>
          ) : null}
        </form>
      )}
    </Modal>
  )
}

export { InviteUserDialog }
